import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ThingModalEvent} from '@events/ThingModalEvent';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ThingsUtil} from '../../../util/things';
import {THINGS_ATTRIBUTES_CONFIG} from '../../../constants/things.constants';
import {ParkService} from "@core/park/park.service";
import { RedrawMapEvent } from '@events/RedrawMapEvent';
import { LatLongEvent } from '@events/LatLongEvent';
import { ThingTypeEvent } from '@events/things/ThingTypeEvent';
import { ThingConfig } from '@core/thing/thingConfig';
import {animate, state, style, transition, trigger} from "@angular/animations";
import { ThingService } from '@core/thing/thing.service';
import {RestroomOpeningComponent} from "@components/things/restroomOpening/restroomOpening.component";
import {KngActivityIndicatorService} from "@layout/kingswell/services/kngActivityIndicator.service";
import {Subscription} from "rxjs/Subscription";

interface MyFormGroup {
  [key: string]: any;
}

export interface FormConfig {
  hasAttributes: boolean;
}

@Component({
  selector: 'things-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.less'],
  animations: [
    trigger('showEditThingAnimation', [
      state('*', style({
      })),
      state('void', style({
        transform: 'translate3d(-200px, 0, 0)'
      })),
      transition('void <=> *', animate('200ms ease-in-out')),
    ])
  ]
})
export class ThingsFormComponent implements OnInit, OnDestroy {

  @Input()
  public key: string;
  @Input()
  public title: string;
  @Input()
  public onSubmit: Function;
  @Input()
  public thingType: string;
  @Input()
  public config: ThingConfig;

  public open: boolean = false;
  public form: FormGroup;
  public types: Array<any> = [];

  public worlds: Array<any> = [];
  public kingdoms: Array<any> = [];
  public areas: Array<any> = [];

  public isEdit: boolean = false;
  public thingName: string;

  public thing: any;

  public restroomOpeningModel: any;

  public statuses: Array<any> = [];

  public toggleOn: boolean = false;

  public attributeConfig: Array<any>;

  public areaLat: Number;
  public areaLon: Number;
  public showLatLonFields: boolean = false;

  public hasNameSuffix: boolean = false;

  @ViewChild(RestroomOpeningComponent) restroomOpeningComponent: RestroomOpeningComponent;

  public showHeaderBottomLine:boolean = false;

  private thingModalSubscription: Subscription;

  constructor(private thingModal: ThingModalEvent, private fb: FormBuilder, private thingService: ThingService,
              private park: ParkService, private thingsUtil: ThingsUtil, private redrawMap: RedrawMapEvent,
              private latLngEvent: LatLongEvent, private thingTypeEvent: ThingTypeEvent,
              private kngActivityIndicator: KngActivityIndicatorService) {
    this.form = this.fb.group(this.getFormMap());
  }

  getThingType() {
    if (!this.config) {
      return (this.thing && !this.thing.type) ? this.thing.type.toLowerCase : '';
    }

    if (this.config.name === 'cans') {
      return 'can';
    } else if (this.config.name === 'restrooms') {
      return 'restrooms';
    } else if (this.config.name === 'time-based') {
      return 'time-based';
    } else if (this.config.name === 'resources') {
      return 'resource';
    }

    return '';
  }

  getFormMap(thing: any = {}) : MyFormGroup {
    let form = {
      name: [thing.name, Validators.required],
      id: [thing.id, Validators.required],
      type: [thing.type, Validators.required],
      active: [!! thing.active, Validators.required],
      state: [!! thing.state, Validators.required],
      world: [thing.world, Validators.required],
      kingdom: [thing.kingdom, Validators.required],
      area: [thing.area, Validators.required],
      lat: [thing.lat, Validators.required],
      lon: [thing.lon, Validators.required],
      deploymentThreshold: [thing.deploymentThreshold]
    }

    if (! this.isEdit && this.thingType) {
      // do something here
    }

    return form as MyFormGroup;
  }

  ngOnInit() {
    this.thingModalSubscription = this.thingModal.on().subscribe(data => {
      if (data.key === this.key) {
        const thing = data.thing;
        this.thing = Object.assign({}, thing);
        this.thingType = this.getThingType();
        Promise.all([this.thingService.getThingDefaults(this.thingType), this.park.getWorldsKingdomsLands()]).then(data => {
          const defaults = data[0];
          const worlds = data[1].worlds;

          this.worlds = worlds;
          this.kingdoms = this.worlds[0].kingdoms;
          this.areas = this.kingdoms[0].lands;

          if (! this.config && this.thing.type) {
            this.config = this.thingsUtil.getConfig(this.thing);
          }

          // Per S-429336, we are no longer adding a suffix to the name
          // if (this.thingType === 'resource') {
          //   this.hasNameSuffix = true;
          // }

          if (thing.thingId) {
            // edit

            const segments = thing.area.split('-');
            const worldId = segments[0];
            const kingdomId = segments[1];

            if (this.hasNameSuffix) {
              let bacon: string = thing.name.toLowerCase();
              let temp = bacon.lastIndexOf(this.thingsUtil.getLabelForType(thing.type).toLowerCase());
              if (temp > -1) {
                thing.name = thing.name.substr(0, temp).trim();
              }
            }

            this.isEdit = true;
            this.thingName = thing.name || thing.thingId;
            this.thingType = thing.type === 'RESTROOM_MALE' || thing.type === 'RESTROOM_FEMALE' ? 'restroom' : 'can';

            let selectedWorld = this.worlds.find(world => world.worldId === worldId);
            if (selectedWorld) {
              this.kingdoms = selectedWorld.kingdoms;
              let selectedKingdom = this.kingdoms.find(kingdom => kingdom.kingdomId === kingdomId);
              if (selectedKingdom) {
                this.areas = selectedKingdom.lands;
              }
            }

            this.form = this.fb.group(this.getFormMap({
              world: worldId,
              kingdom: kingdomId,
              area: thing.area,
              lat: thing.lat,
              lon: thing.lon,
              name: thing.name,
              id: thing.thingId,
              type: thing.type,
              active: ! thing.deactivated,
              state: this.thingsUtil.isRestroom(thing) || this.thingsUtil.isTimeBasedTask(thing) ? ! thing.closed : null
            }));

            //this.form.get('world').disable();
            //this.form.get('kingdom').disable();
          } else {
            let defaultType = this.getTypes() && this.getTypes()[0];

            let world = this.worlds[0].worldId;
            let kingdom = this.worlds[0].kingdoms[0].kingdomId;
            let area = this.worlds[0].kingdoms[0].lands[0].areaId;

            if( thing.areaId ) {
              // if areaId exists, find the area by area ID from the WorldKingdomLand Object
              let areaObj = data[1].areaMap.get(thing.areaId);
              if( areaObj ){
                world = areaObj.worldId;
                kingdom = areaObj.kingdomId;
                area = areaObj.areaId;
              }
            }

            let selectedWorld = this.worlds.find(w => w.worldId === world);
            if (selectedWorld) {
              this.kingdoms = selectedWorld.kingdoms;
              let selectedKingdom = this.kingdoms.find(k => k.kingdomId === kingdom);
              if (selectedKingdom) {
                this.areas = selectedKingdom.lands;
              }
            }

            this.form = this.fb.group(this.getFormMap({
              deploymentThreshold: defaults.deploymentThreshold,
              world: world,
              kingdom: kingdom,
              area: area,
              active: false,
              state: false,
              type: defaultType && defaultType.value
            }));

            // Add restroom opening form into parent form
            setTimeout(() => {
              if( this.restroomOpeningComponent ) {
                this.form.addControl('restroomOpeningForm', this.restroomOpeningComponent.form);
                this.restroomOpeningComponent.form.setParent(this.form);
              }
            }, 0);
          }

          this.updateAreaLatLon();

          this.types = this.getTypes();
          this.statuses = this.getStatuses();
          if (this.config.form.hasAttributes) {
            this.attributeConfig = this.getAttributes(this.types[0].value);
            this.buildAttributesControls();
          }

          this.latLngEvent.on().subscribe(data => {
            if (data.key === 'thingsMap.update') {
              this.form.controls['lat'].setValue(data.lat);
              this.form.controls['lat'].updateValueAndValidity();
              this.form.controls['lon'].setValue(data.lon);
              this.form.controls['lon'].updateValueAndValidity();
            }
          })

          this.open = true;

          this.restroomOpeningModel = {
            openingTimeDelay: this.thing.openingTimeDelay
          };
        });
      }
    });
  }

  ngOnDestroy() {
    if (this.thingModalSubscription)
      this.thingModalSubscription.unsubscribe();
  }

  getTitle() {
    return this.title + (this.isEdit ? this.thingName : '');
  }

  getTypes() {
    return this.config.form.types;
  }

  getStatuses() {
    return [
      {
        label: 'Active',
        value: true
      },
      {
        label: 'Inactive',
        value: false
      }
    ];
  }

  getAttributes(type) {
    return THINGS_ATTRIBUTES_CONFIG[type];
  }

  isOpen() {
    return this.open;
  }

  close() {
    this.open = false;
    this.kngActivityIndicator.close();
  }

  cancel() {
    this.close();
  }

  buildAttributesControls() {
    if (this.config.form.hasAttributes) {
      Object.keys(this.form.controls).map(key => {
        if (key.indexOf('attribute-') > -1 && ! this.attributeConfig.find(c => c.key === key.replace('attribute-', ''))) {
          this.form.removeControl(key);
        }
      });

      this.attributeConfig.map(attr => {
        this.form.addControl('attribute-' + attr.key, new FormControl(''));
      });
    }
  }

  floatOrNull(val) {
    let newVal = parseFloat(val);
    return isNaN(newVal) ? null : newVal;
  }

  submitForm(value: any) {
    this.kngActivityIndicator.show()
    let thing:any = {
      thingId: value.id,
      area: value.area,
      name: value.name,
      type: value.type,
      lat: this.floatOrNull(value.lat),
      lon: this.floatOrNull(value.lon)
    }

    if (this.hasNameSuffix) {
      thing.name = thing.name + ' ' + this.thingsUtil.getLabelForType(thing.type).toUpperCase();
    }

    let attributes = {};

    Object.keys(value).map(key => {
      if (key.indexOf('attribute-') > -1) {
        attributes[key.replace('attribute-', '')] = !! value[key];
      }
    });

    if (this.thingsUtil.isCan(thing)) {
      thing.deactivated = !value.active;
    } else if (this.thingsUtil.isRestroom(thing) || this.thingsUtil.isTimeBasedTask(thing)) {
      // add thing.deactivated = !value.status
      thing.deactivated = !value.active;
      if(thing.deactivated){
        thing.closed = true;
      } else {
        thing.closed = !value.state;
      }
    }

    if (! this.isEdit) {
      if (this.config.form.hasThreshold !== false) {
        thing.thresholdConfig = [
          {
            start: '06:00',
            end: '30:00',
            threshold: parseInt(value.deploymentThreshold)
          }
        ];
      }

      if( this.thingsUtil.isRestroom(thing) || this.thingsUtil.isTimeBasedTask(thing) ){
        if( this.restroomOpeningModel.openingTime ){
          thing.openingTimeDelay = null;
          thing.openingTime = this.restroomOpeningModel.openingTime;
        }else if (this.restroomOpeningModel.openingTimeDelay || this.restroomOpeningModel.openingTimeDelay == 0) {
          thing.openingTimeDelay = this.floatOrNull(this.restroomOpeningModel.openingTimeDelay);
          thing.openingTime = '';
        }
      }

      if (this.config.form.hasAttributes) {
        thing.attributes = attributes;
      }
    } else {
      // TODO: Find a better way than this
      this.thing.area = thing.area;
      this.thing.name = thing.name;
      this.thing.type = thing.type;
      this.thing.lat = this.floatOrNull(thing.lat);
      this.thing.lon = this.floatOrNull(thing.lon);
      if (this.thingsUtil.isCan(thing)) {
        this.thing.deactivated = ! value.active;
      } else {
        // add this.thing.deactivated = ! value.status
        this.thing.deactivated = ! value.active;
        if(this.thing.deactivated){
          this.thing.closed = true;
        } else {
          this.thing.closed = !value.state;
        }
      }
      thing = this.thing;
    }

    if (this.onSubmit) {
      this.onSubmit(thing).then(res => {
        this.close();
      }).catch(err => {
        let code: String = "" + (err && err.error && err.error.code);

        if (code === 'ThingNameNotUniqueError') {
          let control: FormControl = this.form.get('name') as FormControl;
          control.setErrors({'nameAlreadyExists': true});
          control.markAsTouched();
        }
        this.kngActivityIndicator.close()
      });
    }else{
      this.kngActivityIndicator.close()
    }
  }

  toggleSwitch() {
    this.toggleOn = ! this.toggleOn;
  }

  onTypeChange($event) {
    let type = this.form.controls['type'].value;
    this.attributeConfig = this.getAttributes(type);
    this.buildAttributesControls();
    this.thingTypeEvent.fire(type);
  }

  onAreaChange() {
    if (! this.isEdit) {
      this.updateAreaLatLon();
      this.latLngEvent.fire('thingsDetailMap.pan', this.areaLat, this.areaLon);
    }
  }

  updateAreaLatLon() {
    var selectedArea = this.areas.find(area => area.areaId === this.form.controls['area'].value) || {};
    this.areaLat = selectedArea.lat ? selectedArea.lat : 28.418749;
    this.areaLon = selectedArea.lon ? selectedArea.lon : -81.581211;
  }

  toggleShowLatLonFields($event) {
    $event.preventDefault();
    $event.stopPropagation();

    this.showLatLonFields = ! this.showLatLonFields;
  }

  previewLatLonChanges() {
    this.latLngEvent.fire('thingsMap.addPreviewMarker', this.form.controls['lat'].value, this.form.controls['lon'].value);
  }

  onWorldChange() {
    let worldId = this.form.get('world').value;
    let world = this.worlds.find(world => world.worldId === worldId);

    if (world) {
      this.kingdoms = world.kingdoms;
      let kingdomControl = this.form.get('kingdom') as FormControl;
      let kingdomId = this.kingdoms && this.kingdoms[0] && this.kingdoms[0].kingdomId;
      kingdomControl.setValue(kingdomId);
      this.onKingdomChange();
    }
  }

  onKingdomChange() {
    let kingdomId = this.form.get('kingdom').value;
    let kingdom = this.kingdoms.find(kingdom => kingdom.kingdomId === kingdomId);

    if (kingdom) {
      this.areas = kingdom.lands;
      let areaControl = this.form.get('area') as FormControl;
      let areaId = this.areas && this.areas[0] && this.areas[0].areaId;
      areaControl.setValue(areaId);
      this.onAreaChange();
    }
  }

  onContentScroll(scrollTop){
    this.showHeaderBottomLine = ( scrollTop > 0 );
  }
}



// WEBPACK FOOTER //
// D:/dclean-web-latest/cot-web/src/app/components/things/form/form.component.ts